import apiClient from './apiClient';

export interface AudioAnalysis {
  id: number;
  tune_id: number;
  extraction_status: string;
  extraction_error: string | null;
  extracted_at: string | null;

  // Technical metadata
  duration: number | null;
  bitrate: number | null;
  sample_rate: number | null;
  bit_depth: number | null;
  channels: number | null;
  filesize: number | null;

  // ID3 tags
  tag_title: string | null;
  tag_artist: string | null;
  tag_year: string | null;
  tag_track_number: number | null;
  tag_track_total: number | null;
  tag_disc_number: number | null;
  tag_disc_total: number | null;
  tag_genre: string | null;
  tag_composer: string | null;
  tag_bpm: number | null;
  tag_isrc: string | null;
  tag_copyright: string | null;

  // Audio features
  detected_bpm: number | null;
  bpm_confidence: number | null;
  beat_positions: number[] | null;
  detected_key: string | null;
  detected_scale: string | null;
  key_confidence: number | null;
  tuning_frequency: number | null;
  loudness_integrated: number | null;
  loudness_range: number | null;
  dynamic_range: number | null;

  // Fingerprints
  chromaprint_fingerprint: string | null;
  chroma_vector: number[] | null;
  spectral_peaks: Array<{time: number; freq: number; magnitude: number}> | null;
}

export interface TuneWithAnalysis {
  id: number;
  user_id: string;
  user_email: string;
  video_title: string;
  youtube_id: string | null;
  upload_date: string | null;
  date_created: string;
  scheduled: boolean | null;
  privacy_status: string;
  category: string;
  tags: string;
  playlist_id: string | null;

  audio_analysis: AudioAnalysis | null;

  benchmark_ffmpeg_time: number | null;
  benchmark_upload_time: number | null;
  benchmark_total_time: number | null;
}

export interface TunesSearchResponse {
  tunes: TuneWithAnalysis[];
  total: number;
  limit: number;
  offset: number;
}

export interface SimilarTune {
  id: number;
  user_id: string;
  video_title: string;
  youtube_id: string | null;
  similarity_score: number;
  detected_bpm: number | null;
  detected_key: string | null;
  detected_scale: string | null;
  tag_genre: string | null;
  tag_artist: string | null;
}

export interface SimilarTunesResponse {
  source_tune_id: number;
  source_tune_title: string;
  similar_tunes: SimilarTune[];
  limit: number;
}

export interface SearchTunesParams {
  limit?: number;
  offset?: number;

  // BPM filters
  bpm_min?: number;
  bpm_max?: number;

  // Key/Scale filters
  key?: string;
  scale?: string;

  // Loudness filters
  loudness_min?: number;
  loudness_max?: number;

  // ID3 tag filters
  genre?: string;
  artist?: string;
  album?: string;
  year?: string;

  // Duration filters
  duration_min?: number;
  duration_max?: number;

  // Flags
  has_fingerprint?: boolean;
  has_chroma_vector?: boolean;

  // User filter
  user_id?: string;

  // Status filter
  extraction_status?: string;
}

export interface FindSimilarParams {
  limit?: number;
  user_id?: string;
  min_similarity?: number;
}

export const adminTunesService = {
  /**
   * Search tunes with audio analysis filters
   */
  searchTunes: async (params?: SearchTunesParams): Promise<TunesSearchResponse> => {
    const response = await apiClient.get('/admin/tunes', { params });
    // Backend returns { title, message, data: { tunes, total, limit, offset } }
    const responseData = response.data.data;
    return {
      tunes: responseData.tunes || [],
      total: responseData.total || 0,
      limit: responseData.limit || 50,
      offset: responseData.offset || 0
    };
  },

  /**
   * Get a single tune with its audio analysis
   */
  getTuneById: async (tuneId: number): Promise<TuneWithAnalysis> => {
    const response = await apiClient.get(`/admin/tunes/${tuneId}`);
    return response.data.data;
  },

  /**
   * Find tunes similar to the given tune
   */
  findSimilarTunes: async (tuneId: number, params?: FindSimilarParams): Promise<SimilarTunesResponse> => {
    const response = await apiClient.get(`/tunes/${tuneId}/similar`, { params });
    return response.data.data;
  }
};
